/**
 * SplineMeshComponent generates a flat ribbon mesh along a Catmull-Rom spline.
 * Use cases include roads, paths, rivers, and any geometry that follows a curved path.
 * Samples points along the spline and creates a ribbon with proper UV mapping.
 */
import * as THREE from 'three';

import { MaterialUtils } from '../../materials.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { isNode } from '../../utils/Utils.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';
import { SceneComponent } from '../SceneComponent.js';

import type { GenericMaterialType } from '../../materials.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { MaterialPath } from '../../utils/serialization/decorator.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';

export interface SplineMeshComponentOptions extends PrimitiveComponentOptions {
  points?: THREE.Vector3[];
  width?: number;
  segments?: number;
  closed?: boolean;
  tension?: number;
  uvScale?: number;
  material?: GenericMaterialType;
  showDebug?: boolean;
}

@EngineClass('SplineMeshComponent')
export class SplineMeshComponent extends PrimitiveComponent {


  @property()
  public points: THREE.Vector3[] = [
      new THREE.Vector3(-2, 0, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(2, 0, 0),
    ];

  @property({ type: 'number', min: 0.1, max: 100, step: 0.1, decimals: 2 })
  public width: number = 1;

  @property({ type: 'number', min: 2, max: 500, step: 1 })
  public segments: number = 50;

  @property()
  public closed: boolean = false;

  @property({ type: 'number', min: 0, max: 1, step: 0.05, decimals: 2 })
  public tension: number = 0.5;

  @property({ type: 'number', min: 0.01, max: 10, step: 0.1, decimals: 2 })
  public uvScale: number = 1;

  @property()
  public materialPath: MaterialPath | null = null;

  @property()
  public showDebug: boolean = false;

  private mesh: THREE.Mesh | null = null;
  /** Runtime material instance (not serialized) */
  private runtimeMaterial: THREE.Material | THREE.Material[] | null = null;
  private curve: THREE.CatmullRomCurve3 | null = null;
  private debugGroup: THREE.Group | null = null;


  public override initialize(options?: SplineMeshComponentOptions): void {
    // Set default physics options
    const defaultOptions: SplineMeshComponentOptions = {
      physicsOptions: {
        ...PrimitiveComponent.DEFAULT_PHYSICS_OPTIONS,
        enabled: false,
      },
      ...options
    };

    super.initialize(defaultOptions);

    this.points = options?.points ?? this.points;
    this.width = options?.width ?? this.width;
    this.segments = options?.segments ?? this.segments;
    this.closed = options?.closed ?? this.closed;
    this.tension = options?.tension ?? this.tension;
    this.uvScale = options?.uvScale ?? this.uvScale;
    if (options?.material !== undefined) {
      if (typeof options.material === 'string') {
        this.materialPath = options.material;
      } else {
        this.runtimeMaterial = options.material;
      }
    }
    this.showDebug = options?.showDebug ?? this.showDebug;

    this.rebuildMesh();
  }

  public override postLoad(): void {
    super.postLoad();
    this.rebuildMesh();
  }

  public getMesh(): THREE.Mesh | null {
    return this.mesh;
  }

  public getCurve(): THREE.CatmullRomCurve3 | null {
    return this.curve;
  }

  public setPoints(points: THREE.Vector3[]): void {
    this.points = points;
    this.rebuildMesh();
  }

  public setWidth(width: number): void {
    this.width = width;
    this.rebuildMesh();
  }

  public setSegments(segments: number): void {
    this.segments = segments;
    this.rebuildMesh();
  }

  public setTension(tension: number): void {
    this.tension = tension;
    this.rebuildMesh();
  }

  public setUvScale(uvScale: number): void {
    this.uvScale = uvScale;
    this.rebuildMesh();
  }

  public setClosed(closed: boolean): void {
    this.closed = closed;
    this.rebuildMesh();
  }

  public setMaterialPath(path: string | null): void {
    this.materialPath = path;
    this.runtimeMaterial = null;
    this.applyMaterial();
  }

  /**
   * Sets a runtime material for the mesh (not serialized)
   */
  public setMaterial(material: THREE.Material | THREE.Material[]): void {
    this.materialPath = null;
    this.runtimeMaterial = material;
    this.applyMaterial();
  }

  /**
   * Applies the current material (from path or runtime) to the mesh
   */
  private applyMaterial(): void {
    if (!this.mesh) {
      this.rebuildMesh();
      return;
    }

    if (this.runtimeMaterial) {
      this.mesh.material = this.runtimeMaterial;
    } else if (this.materialPath) {
      getLatest(
        this,
        'materialPath',
        () => resourceManager.loadGenericMaterial(this.materialPath!)
      ).then((loadedMaterial) => {
        if (loadedMaterial && this.mesh) {
          this.mesh.material = loadedMaterial;
        }
      });
    } else {
      this.mesh.material = MaterialUtils.DefaultMaterial;
    }
  }

  /**
   * Toggles debug visualization
   */
  public setShowDebug(show: boolean): void {
    this.showDebug = show;
    this.updateDebugVisualization();
  }

  /**
   * Rebuilds the ribbon mesh based on current options
   */
  public rebuildMesh(): void {
    // Filter out null/undefined points and ensure they are Vector3 instances
    // (points may be plain {x,y,z} objects after deserialization)
    const rawPoints = this.points ?? [];
    const points = rawPoints
      .filter((p): p is THREE.Vector3 => p != null)
      .map(p => p instanceof THREE.Vector3 ? p : new THREE.Vector3((p as any).x, (p as any).y, (p as any).z));
    if (points.length < 2) {
      this.clearMesh();
      return;
    }

    // Create the Catmull-Rom curve
    this.curve = new THREE.CatmullRomCurve3(
      points,
      this.closed,
      'catmullrom',
      this.tension
    );

    const segments = this.segments;
    const width = this.width;
    const uvScale = this.uvScale;
    const halfWidth = width / 2;

    // Generate geometry
    const geometry = this.generateRibbonGeometry(this.curve, segments, halfWidth, uvScale);

    // Remove old mesh if exists
    if (this.mesh) {
      this.mesh.removeFromParent();
      this.mesh.geometry.dispose();
    }

    // Create new mesh with default material initially
    this.mesh = new THREE.Mesh(geometry, MaterialUtils.DefaultMaterial);
    this.mesh.setTransient(true);
    this.bindObject3DProperties(this.mesh, SceneComponent.boundObject3DProperties);
    this.add(this.mesh);

    // Apply material if specified
    if (!isNode()) {
      if (this.runtimeMaterial) {
        // Use runtime material directly
        this.mesh.material = this.runtimeMaterial;
      } else if (this.materialPath) {
        // Load material from path
        getLatest(
          this,
          'materialPath',
          () => resourceManager.loadGenericMaterial(this.materialPath!)
        ).then((loadedMaterial) => {
          if (loadedMaterial && this.mesh) {
            this.mesh.material = loadedMaterial;
          }
        });
      }
    }

    // Update debug visualization
    this.updateDebugVisualization();
  }

  /**
   * Generates a ribbon geometry along the curve
   */
  private generateRibbonGeometry(
    curve: THREE.CatmullRomCurve3,
    segments: number,
    halfWidth: number,
    uvScale: number
  ): THREE.BufferGeometry {
    const vertices: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];

    const totalLength = curve.getLength();
    const up = new THREE.Vector3(0, 1, 0);
    const tempTangent = new THREE.Vector3();
    const tempNormal = new THREE.Vector3();
    const tempBinormal = new THREE.Vector3();

    // Sample points along the curve
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const point = curve.getPointAt(t);
      const tangent = curve.getTangentAt(t).normalize();

      // Calculate binormal (perpendicular to tangent and up)
      tempBinormal.crossVectors(up, tangent).normalize();

      // Handle degenerate case when tangent is parallel to up
      if (tempBinormal.lengthSq() < 0.001) {
        tempBinormal.set(1, 0, 0);
      }

      // Calculate normal (perpendicular to tangent and binormal)
      tempNormal.crossVectors(tangent, tempBinormal).normalize();

      // Create two vertices at this point (left and right edges)
      const leftPoint = point.clone().addScaledVector(tempBinormal, -halfWidth);
      const rightPoint = point.clone().addScaledVector(tempBinormal, halfWidth);

      vertices.push(leftPoint.x, leftPoint.y, leftPoint.z);
      vertices.push(rightPoint.x, rightPoint.y, rightPoint.z);

      // Normals point up
      normals.push(tempNormal.x, tempNormal.y, tempNormal.z);
      normals.push(tempNormal.x, tempNormal.y, tempNormal.z);

      // UVs: U tiles along length based on world distance, V is 0-1 across width
      const distanceAlongCurve = t * totalLength;
      const u = distanceAlongCurve * uvScale;
      uvs.push(u, 0); // Left edge
      uvs.push(u, 1); // Right edge
    }

    // Generate indices for triangle strip (CCW winding for upward-facing normals)
    for (let i = 0; i < segments; i++) {
      const baseIndex = i * 2;

      // First triangle: left[i] -> left[i+1] -> right[i]
      indices.push(baseIndex, baseIndex + 2, baseIndex + 1);
      // Second triangle: right[i] -> left[i+1] -> right[i+1]
      indices.push(baseIndex + 1, baseIndex + 2, baseIndex + 3);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);

    return geometry;
  }

  /**
   * Clears the current mesh
   */
  private clearMesh(): void {
    if (this.mesh) {
      this.mesh.removeFromParent();
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    this.curve = null;
    this.clearDebugVisualization();
  }

  /**
   * Updates debug visualization showing control points and spline
   */
  private updateDebugVisualization(): void {
    this.clearDebugVisualization();

    if (!this.showDebug || !this.curve) {
      return;
    }

    this.debugGroup = new THREE.Group();
    this.debugGroup.setTransient(true);

    // Filter out null/undefined points and ensure they are Vector3 instances
    const rawPoints = this.points ?? [];
    const points = rawPoints
      .filter((p): p is THREE.Vector3 => p != null)
      .map(p => p instanceof THREE.Vector3 ? p : new THREE.Vector3((p as any).x, (p as any).y, (p as any).z));

    // Draw control point spheres
    const sphereGeometry = new THREE.SphereGeometry(0.15);
    const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });

    for (let i = 0; i < points.length; i++) {
      const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      sphere.position.copy(points[i]);
      this.debugGroup.add(sphere);
    }

    // Draw spline curve line
    const curvePoints = this.curve.getPoints(100);
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
    const line = new THREE.Line(lineGeometry, lineMaterial);
    this.debugGroup.add(line);

    // Draw tangent indicators at control points
    const arrowMaterial = new THREE.LineBasicMaterial({ color: 0x0000ff });
    for (let i = 0; i < points.length; i++) {
      const t = this.closed ? i / points.length : i / (points.length - 1);
      const tangent = this.curve.getTangentAt(Math.min(t, 1)).normalize();
      const arrowPoints = [
        points[i].clone(),
        points[i].clone().addScaledVector(tangent, 0.5)
      ];
      const arrowGeometry = new THREE.BufferGeometry().setFromPoints(arrowPoints);
      const arrow = new THREE.Line(arrowGeometry, arrowMaterial);
      this.debugGroup.add(arrow);
    }

    this.add(this.debugGroup);
  }

  /**
   * Clears debug visualization
   */
  private clearDebugVisualization(): void {
    if (this.debugGroup) {
      this.debugGroup.removeFromParent();
      this.debugGroup.traverse((child) => {
        if (child instanceof THREE.Mesh || child instanceof THREE.Line) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.debugGroup = null;
    }
  }

  public override calcBoundingBox(): void {
    if (this.mesh) {
      this.mesh.geometry.computeBoundingBox();
      this._boundingBox.copy(this.mesh.geometry.boundingBox!).applyMatrix4(this.mesh.matrixWorld);
    }
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Path';
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    if (path === 'materialPath') {
      this.setMaterialPath(value);
    } else if (path === 'points' || path.startsWith('points[') || path.startsWith('points.')) {
      // Handle array changes: full replacement, element changes, or property changes
      this.rebuildMesh();
    } else if (['width', 'segments', 'tension', 'uvScale', 'closed', 'showDebug'].includes(path)) {
      this.rebuildMesh();
    }
  }
}

